import React, { useState, useEffect } from 'react';
import { websiteService, Website, TrackingStatus } from '../services/websiteService';
import { getTrackingStatusLabel, getTrackingStatusBadgeClass } from '../utils/trackingStatus';

const WebsiteSettings: React.FC = () => {
  const [website, setWebsite] = useState<Website | null>(null);
  const [trackingStatus, setTrackingStatus] = useState<TrackingStatus | null>(null);
  const [script, setScript] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [domainEdit, setDomainEdit] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [verifyMessage, setVerifyMessage] = useState<string | null>(null);

  const loadWebsite = async () => {
    try {
      const w = await websiteService.getWebsite();
      setWebsite(w);
      setDomainEdit(w.domain);
      const s = await websiteService.getTrackingScript();
      setScript(s.script);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load website');
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async () => {
    try {
      const s = await websiteService.getTrackingStatus();
      setTrackingStatus(s);
      return s;
    } catch (e) {
      setTrackingStatus(null);
      throw e;
    }
  };

  useEffect(() => {
    loadWebsite();
  }, []);

  useEffect(() => {
    if (website) loadStatus();
  }, [website?.id]);

  const handleSaveDomain = async () => {
    if (!domainEdit.trim()) return;
    setActionLoading(true);
    setError('');
    try {
      const w = await websiteService.updateDomain(domainEdit.trim());
      setWebsite(w);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update domain');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerateKey = async () => {
    if (!window.confirm('Regenerating the key will invalidate your current tracking script. You must update the script on your website.')) return;
    setActionLoading(true);
    setError('');
    try {
      const { public_key } = await websiteService.regenerateKey();
      setWebsite((prev) => (prev ? { ...prev, public_key } : null));
      await loadWebsite();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to regenerate key');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyScript = async () => {
    try {
      await navigator.clipboard.writeText(script);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Copy failed');
    }
  };

  const handleVerify = async () => {
    setActionLoading(true);
    setError('');
    setVerifyMessage(null);
    try {
      const s = await loadStatus();
      const hasEvents = !!(s?.last_event_at || (s && s.event_count_last_24h > 0));
      if (hasEvents) {
        setVerifyMessage(`Tracking verified. ${s.event_count_last_24h} events in the last 24 hours.`);
      } else {
        setVerifyMessage(
          'No events received yet. Ensure the script is in your site\'s <body>, the page was loaded after adding it, ' +
          'and your domain is not blocking the request. Try loading your website in a new tab and click Verify again.'
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to verify. Check your connection.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-fg-muted">Loading...</div>
    );
  }

  const statusLabel = getTrackingStatusLabel(trackingStatus, website?.last_event_at ?? null);
  const statusColor = getTrackingStatusBadgeClass(statusLabel);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Website Settings</h1>
      <div className="rounded-xl border border-border bg-surface p-4 text-sm text-fg-muted">
        Organization profile is the source of truth. Keep core business identity in Organization Settings, and use Website Settings for channel-specific behavior.
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
      {verifyMessage && (
        <div className={`p-4 rounded-lg border ${
          trackingStatus?.last_event_at
            ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300'
            : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300'
        }`}>
          {verifyMessage}
        </div>
      )}

      {/* Section 1: Website Info */}
      <div className="bg-surface rounded-lg shadow border border-border p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Website Info</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-fg-secondary mb-1">Domain</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={domainEdit}
                onChange={(e) => setDomainEdit(e.target.value)}
                placeholder="example.com"
                className="flex-1 px-3 py-2 border border-border rounded-lg bg-input text-gray-900 dark:text-white"
              />
              <button
                onClick={handleSaveDomain}
                disabled={actionLoading}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-fg-secondary mb-1">Public Key</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded font-mono text-sm truncate">
                {website?.public_key ?? '-'}
              </code>
              <button
                onClick={handleRegenerateKey}
                disabled={actionLoading}
                className="px-4 py-2 border border-amber-500 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50"
              >
                Regenerate Key
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Tracking Script */}
      <div className="bg-surface rounded-lg shadow border border-border p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tracking Script</h2>
        <p className="text-sm text-fg-secondary mb-2">
          Add this script to your website, just before the closing <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">&lt;/body&gt;</code> tag.
        </p>
        <p className="text-sm text-fg-secondary mb-2">
          For contact forms to create Leads, include <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">email</code> (required), plus <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">name</code>, <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">phone</code>, <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">company</code>, <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">message</code>, and <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">service_interest</code> as needed.
        </p>
        <div className="relative">
          <pre className="p-4 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto text-xs overflow-y-auto max-h-64">
            {script || 'Loading...'}
          </pre>
          <button
            onClick={handleCopyScript}
            className="absolute top-2 right-2 px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Lead capture API (tenant API key — create in Django admin) */}
      <div className="bg-surface rounded-lg shadow border border-border p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Lead capture API</h2>
        <p className="text-sm text-fg-secondary mb-4">
          Submit form data from your own site with a per-tenant API key (create under Django admin → Tenant API keys).
          Use <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">destination: &quot;inbound&quot;</code> to
          store rows in the <strong>Inbound</strong> tab; omit or use <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">pipeline</code> for
          pipeline leads (same as the default).
        </p>
        <p className="text-sm text-fg-secondary mb-2">
          Endpoint: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">POST /api/public/leads/</code>{' '}
          (same base URL as the app API). Header: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">X-API-Key: lp_…</code>
        </p>
        <pre className="p-4 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto text-xs">
{`{
  "full_name": "Jane Doe",
  "email": "jane@example.com",
  "phone": "+15551234567",
  "company": "Acme LLC",
  "message": "Interested in a demo",
  "source": "Contact form",
  "destination": "inbound",
  "utm_source": "google",
  "utm_medium": "cpc",
  "utm_campaign": "spring"
}`}
        </pre>
      </div>

      {/* Section 3 & 4: Tracking Status + Verify */}
      <div className="bg-surface rounded-lg shadow border border-border p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tracking Status</h2>
        <div className="flex items-center gap-4 flex-wrap">
          <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${statusColor}`}>
            {statusLabel}
          </span>
          {trackingStatus?.last_event_at && (
            <span className="text-sm text-fg-muted">
              Last event: {new Date(trackingStatus.last_event_at).toLocaleString()}
            </span>
          )}
          {trackingStatus && (
            <span className="text-sm text-fg-muted">
              Events (24h, raw): {trackingStatus.event_count_last_24h}
            </span>
          )}
          <button
            onClick={handleVerify}
            disabled={actionLoading}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
          >
            Verify Installation
          </button>
        </div>
      </div>
    </div>
  );
};

export default WebsiteSettings;
